import { useRef, useState } from "react";
import { Download, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const ENTITIES = [["services", "Service catalog (500)"], ["requests", "Service requests"], ["users", "Users"], ["integrations", "Integrations"], ["audit", "Audit log (SIEM export)"]];

export default function PortalData() {
  const { reloadCatalog } = useApp();
  const nav = useNavigate();
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const download = async (entity, fmt) => {
    try {
      const res = await api.get(`/portal/export/${entity}?fmt=${fmt}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `qatar_one_${entity}.${fmt}`;
      a.click();
    } catch (e) {
      if ([401, 403].includes(e?.response?.status)) nav("/admin");
      else toast.error(errMsg(e));
    }
  };
  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post("/portal/import", fd);
      setResult(data.summary);
      reloadCatalog();
      toast.success("Import complete – catalog cache invalidated");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); fileRef.current.value = ""; }
  };
  return (
    <div className="space-y-6" data-testid="portal-data">
      <div><h1 className="text-2xl font-bold md:text-3xl">Import / export</h1><p className="text-sm text-muted-foreground">Only PLATFORM_SUPER_ADMIN (after the full chain) can upload or download content. Every action is audited.</p></div>
      <section className="rounded-2xl border bg-card p-5 shadow-sm">
        <h2 className="mb-1 text-base font-bold md:text-lg">Import seed data</h2>
        <p className="mb-3 text-xs text-muted-foreground">Upload the Qatar One seed workbook (.xlsx, all sheets) or a services .csv. Import is idempotent (upsert by ID).</p>
        <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="import-file-input" />
        <button onClick={() => fileRef.current.click()} disabled={busy} data-testid="import-upload-btn" className="inline-flex h-11 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Choose file
        </button>
        {result && <pre className="mt-3 rounded-xl bg-muted p-3 text-xs" data-testid="import-result">{JSON.stringify(result, null, 2)}</pre>}
      </section>
      <section className="rounded-2xl border bg-card p-5 shadow-sm">
        <h2 className="mb-3 text-base font-bold md:text-lg">Export</h2>
        <ul className="divide-y">
          {ENTITIES.map(([k, label]) => (
            <li key={k} className="flex flex-wrap items-center justify-between gap-2 py-3">
              <span className="text-sm font-medium">{label}</span>
              <span className="flex gap-2">
                {["csv", "xlsx"].map((fmt) => (
                  <button key={fmt} onClick={() => download(k, fmt)} data-testid={`export-${k}-${fmt}-btn`} className="inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold uppercase hover:border-primary"><Download size={13} /> {fmt}</button>
                ))}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
